<template>
  <div class="content container">
    <div v-if="showPageSizeSelector" class="vehicle-list-toolbar">
      <label class="vehicle-list-page-size-label" for="vehicle-list-page-size">
        {{ t('vehicles-per-page') }}
      </label>
      <select
          id="vehicle-list-page-size"
          class="vehicle-list-page-size-select"
          :value="pageSize"
          @change="onPageSizeChange"
      >
        <option
            v-for="option in pageSizeOptions"
            :key="option"
            :value="option"
        >
          {{ option }}
        </option>
      </select>
    </div>

    <div v-for="vehicle in vehicles" :key="vehicle.id" class="vehicles-list">
      <div
          class="row vehicle-panel"
          :class="[
          { violation: hasViolation(vehicle) },
          { 'no-right-panel': isSG }
        ]"
          @contextmenu.prevent="onRightClick(vehicle)"
      >
        <!-- IMAGE -->
        <div class="image">
          <VehicleImage
              :vehicle="vehicle"
              :image-service="vehService"
              source="vehicle-list"
          />
        </div>

        <!-- LEFT PANEL -->
        <div
            class="measurement-row"
            :class="{ violation: hasViolation(vehicle) }"
            @click="handleGoToDetails(vehicle)"
        >
          <!-- HEADER -->
          <div class="row align-items-center">
            <div class="col-4 measurement-row-header vehicle-header">
              {{ vehicle.location.shortName }}
            </div>

            <div class="col-4 plate-item">
              <div class="row measurement-row-header">
                <div
                    class="col-2 plate-flag center-aligned"
                    :style="{ backgroundColor: getCode(vehicle.nationality) ? '' : 'white' }"
                >
                  <country-flag :country="getCode(vehicle.nationality)" size="small" />
                  <div class="country-code">
                    {{ getCode(vehicle.nationality) || '' }}
                  </div>
                </div>
                <div class="col-8 plate">{{ getPlate(vehicle) }}</div>
              </div>
            </div>

            <div class="col-2 measurement-row-header-thin center-aligned">
              {{ getFormattedDate(vehicle.createdAt) }}
            </div>
            <div class="col-2 measurement-row-header-thin center-aligned">
              {{ getFormattedTime(vehicle.createdAt) }}
            </div>
          </div>

          <!-- TOTAL WEIGHT -->
          <div v-if="shouldShowTotalWeight" class="row measurement-row-row">
            <div class="col-8 measurement-row-field vehicle-header-2">
              {{ t('total-weight') }} [limit {{ vehicle.limitTotalLoad }} kg]
            </div>
            <div :class="['col-4 field-center', getWeightClassName(vehicle)]">
              {{ vehicle.totalLoad }} kg
            </div>
          </div>

          <!-- LENGTH -->
          <div v-if="shouldShowLength" class="row measurement-row-row">
            <div class="col-8 measurement-row-field vehicle-header-2">
              {{ t('length') }}
            </div>
            <div class="col-4 field-center">
              {{ vehicle.vehLength }} cm
            </div>
          </div>

          <!-- HEIGHT -->
          <div v-if="shouldShowHeight" class="row measurement-row-row">
            <div class="col-8 measurement-row-field vehicle-header-2">
              {{ t('height') }}
            </div>
            <div :class="['col-4 field-center', getHeightClassName(vehicle)]">
              <span v-if="vehicle.vehHeight != null">
                {{ vehicle.vehHeight }} cm
              </span>
              <span v-else>
                {{ vehicle.overheight ? 'TAK' : 'NIE' }}
              </span>
            </div>
          </div>

          <!-- SPEED + LIMIT -->
          <div v-if="shouldShowSpeedLimit" class="row measurement-row-row">
            <div class="col-8 measurement-row-field vehicle-header-2">
              {{ t('speed') }}
            </div>
            <div class="col-4 field-center">
              {{ vehicle.speed }} km/h
            </div>
          </div>

          <!-- NUMBER OF AXLES -->
          <div v-if="shouldShowNumberOfAxles" class="row measurement-row-row">
            <div class="col-8 measurement-row-field vehicle-header-2">
              {{ t('number-of-axles') }}
            </div>
            <div class="col-4 field-center">
              {{ vehicle.numberOfAxles }}
            </div>
          </div>

          <!-- CATEGORY CTG8P1 -->
          <div v-if="shouldShowCtg8p1" class="row measurement-row-row">
            <div class="col-8 measurement-row-field vehicle-header-2">
              {{ t('category') }}
            </div>
            <div class="col-4 field-center">
              {{ vehicle.ctg8p1 }}
            </div>
          </div>

          <!-- NATIONALITY -->
          <div v-if="shouldShowNationality" class="row measurement-row-row">
            <div class="col-8 measurement-row-field vehicle-header-2">
              {{ t('nationality') }}
            </div>
            <div class="col-4 field-center">
              {{ vehicle.nationality || '-' }}
            </div>
          </div>

          <!-- DIRECTION / WRONG DIRECTION -->
          <div v-if="shouldShowDirectionWrongDirection" class="row measurement-row-row">
            <div class="col-8 measurement-row-field vehicle-header-2">
              {{ t('direction-and-wrong-way') }}
            </div>
            <div class="col-4 field-center">
              {{ formatYesNo(vehicle.direction) }} / {{ formatYesNo(vehicle.wrongDirection) }}
            </div>
          </div>

          <!-- VIOLATIONS -->
          <div v-if="shouldShowViolationsFlags" class="row measurement-row-row">
            <div class="col-8 measurement-row-field vehicle-header-2">
              {{ t('violations-flags') }}
            </div>
            <div :class="['col-4 field-center', hasAnyViolationFlag(vehicle) ? 'measurement-row-field-b-red' : 'measurement-row-field-b']">
              {{ formatViolationFlags(vehicle) }}
            </div>
          </div>
        </div>

        <!-- RIGHT PANEL -->
        <div
            v-if="!isSG"
            class="load"
            :class="{ violation: hasViolation(vehicle) }"
            @click="handleGoToDetails(vehicle)"
        >
          <div class="row measurement-row-header">
            <div
                class="items-centered center-aligned top-pad"
                style="display: flex; align-items: center; gap: 50px; margin-left: 5px;"
            >
              <CountdownTimer
                  :deadline="moment(vehicle.checkpointTime).format('YYYY/MM/DD HH:mm:ss')"
                  :server-diff="momentDiffServer"
                  :obj-number="String(vehicle.id)"
              />
              <div style="display: flex; align-items: center; gap: 8px;">
                <span style="padding-bottom: 5px;">{{ t('axle-load').toUpperCase() }} [t]</span>
                <span
                    v-if="selectedVehicleIds.includes(vehicle.id)"
                    class="checkmark"
                    style="padding-bottom: 5px;"
                >
                  ✔
                </span>
              </div>
            </div>
          </div>

          <div class="row measurement-row-body centered-flex" v-if="existsAxlesRow1(vehicle.axles)">
            <div class="col-1 axle-1">
              <div class="row-cols-3 axle-field">{{ t('axle') }}:</div>
              <div class="row-cols-6">
                <div class="icon-a"></div>
              </div>
              <div class="row-cols-3 axle-field">t:</div>
            </div>
            <div
                v-for="ax in getAxlesRow1(vehicle.axlesComplex.axlesGroups)"
                :key="ax"
                :class="['col-1', 'width width-' + ax.type]"
                v-html="getDivs(ax)"
            ></div>
          </div>

          <div v-if="existsAxlesRow2(vehicle.axles)" class="row" id="axles8to14">
            <div class="col-1">
              <div class="row-cols-3 axle-field">{{ t('axle') }}:</div>
              <div class="row-cols-6">
                <div class="icon-a"></div>
              </div>
              <div class="row-cols-3 axle-field">t:</div>
            </div>
            <div v-for="ax in getAxlesRow2(vehicle.axles)" :key="ax" class="col-1">
              <div class="items-centered-axles center-aligned">
                <div :class="'row-cols-3 ' + getAxisNumberClass(ax)">
                  {{ ax.type }}
                </div>
                <div class="row-cols-6">
                  <div :class="'icon-a ' + getAxisClass(ax)"></div>
                </div>
                <div :class="'row-cols-3 ' + getAxisNumberClass(ax)">
                  {{ ax.totalLoad }}
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import CountryFlag from 'vue-country-flag-next'
import getCountryISO2 from 'country-iso-3-to-2'
import VehicleImage from '@/components/VehicleImage'
import CountdownTimer from '@/components/current/countdown-timer'
import ArchivalMeasurementsService from '@/services/ArchivalMeasurementsService'
import { useUserStore } from '@/stores/user.store'
import { isSG } from '@/config/appVersion'

moment.locale('pl')

export default {
  name: 'VehicleList',

  components: {
    VehicleImage,
    CountdownTimer,
    CountryFlag,
  },

  props: {
    vehicleList: { required: true },
    dataService: { required: true },
    detailsPath: { default: null },
    goToDetailsView: { type: Function },
    isFromTags: { type: Boolean, default: false },
    showPageSizeSelector: { type: Boolean, default: false },
    pageSize: { type: Number, default: 10 },
    pageSizeOptions: {
      type: Array,
      default: () => [10, 25, 50],
    },
  },
  emits: ['openDetails', 'page-size-change'],

  data() {
    return {
      vehService: this.dataService,
      vehicles: [],
      selectedVehicleIds: [],
      vehicleTags: {},
      taggingInProgress: new Set(),
      userStore: useUserStore(),
      momentDiffServer: 0,
      isSG,
      moment,
    }
  },

  computed: {
    selectedMetricIds() {
      const ids = this.userStore?.preferences?.vehicleListMetricIds
      if (!Array.isArray(ids)) return null

      return ids
        .map((id) => Number(id))
        .filter((id) => Number.isInteger(id) && id > 0)
    },
    metricsVisibilityPref() {
      return this.userStore?.preferences?.vehicleListMetricsVisibility || null
    },
    shouldShowTotalWeight() {
      const ids = this.selectedMetricIds
      if (ids) return ids.includes(1)
      const pref = this.metricsVisibilityPref
      if (!pref) return true
      return pref.totalWeight !== false
    },
    shouldShowLength() {
      const ids = this.selectedMetricIds
      if (ids) return ids.includes(2)
      const pref = this.metricsVisibilityPref
      if (!pref) return true
      return pref.length !== false
    },
    shouldShowHeight() {
      const ids = this.selectedMetricIds
      if (ids) return ids.includes(3)
      const pref = this.metricsVisibilityPref
      if (!pref) return true
      return pref.height !== false
    },
    shouldShowSpeedLimit() {
      const ids = this.selectedMetricIds
      return !!ids && ids.includes(4)
    },
    shouldShowNumberOfAxles() {
      const ids = this.selectedMetricIds
      return !!ids && ids.includes(5)
    },
    shouldShowCtg8p1() {
      const ids = this.selectedMetricIds
      return !!ids && ids.includes(6)
    },
    shouldShowNationality() {
      const ids = this.selectedMetricIds
      return !!ids && ids.includes(7)
    },
    shouldShowDirectionWrongDirection() {
      const ids = this.selectedMetricIds
      return !!ids && ids.includes(8)
    },
    shouldShowViolationsFlags() {
      const ids = this.selectedMetricIds
      return !!ids && ids.includes(9)
    },
  },

  watch: {
    vehicleList: {
      immediate: true,
      handler(newVal = []) {
        this.vehicles = newVal

        const validIds = new Set(newVal.map(v => Number(v.id)))

        this.selectedVehicleIds = this.selectedVehicleIds.filter(id =>
            validIds.has(id)
        )

        Object.keys(this.vehicleTags).forEach(id => {
          if (!validIds.has(Number(id))) delete this.vehicleTags[id]
        })

        if (this.isFromTags) {
          this.selectedVehicleIds = newVal.map(v => Number(v.id))
        }
      },
    },
  },

  async mounted() {
    await this.userStore.self()
    this.momentDiffServer =
        Number(this.userStore?.preferences?.checkpointAdditionalTime || 0) * 1000
  },

  methods: {
    /* ================= DETAILS ================= */

    handleGoToDetails(vehicle) {
      this.$emit('openDetails', vehicle)

      if (this.goToDetailsView) {
        this.goToDetailsView(vehicle)
        return
      }

      if (!this.detailsPath) return

      const path = this.detailsPath.includes('vehicleArch')
          ? `/${this.detailsPath}/${vehicle.id}/${vehicle.location.id}/${this.getCreatedAt(vehicle)}`
          : `/${this.detailsPath}/${vehicle.id}/${vehicle.location.id}`

      this.$router.push({
        path,
        query: {
          backTo: this.$route.fullPath,
        },
      })
    },

    /* ================= TAGGING ================= */

    async onRightClick(vehicle) {
      const id = Number(vehicle.id)

      if (this.taggingInProgress?.has(id)) return
      this.taggingInProgress?.add(id)

      try {
        if (this.selectedVehicleIds.includes(id)) {
          await this.removeTagFlow(vehicle)
        } else {
          await this.addTagFlow(vehicle)
        }
      } catch (e) {
        // ⬇⬇⬇ KLUCZOWE ⬇⬇⬇
        console.error('Tag operation failed:', e)
        // NIC WIĘCEJ – NIE RZUCAMY
      } finally {
        this.taggingInProgress?.delete(id)
      }
    },

    existsAxlesRow1(axles) {
      return axles;
    },
    getAxlesRow1(axles) {
      if (axles) {
        return axles;
      }
    },

    async addTagFlow(vehicle) {
      try {
        const id = Number(vehicle.id)

        const res = await ArchivalMeasurementsService.addTag(
            id,
            vehicle.location.id,
            this.getCreatedAt(vehicle)
        )

        const stillExists = this.vehicles.some(v => Number(v.id) === id)
        if (!stillExists) return

        if (res?.id) {
          this.vehicleTags[id] = res.id
          this.selectedVehicleIds.push(id)
        }
      } catch (e) {
        console.error('addTag failed:', e)
        // ⛔ NIE RZUCAMY
      }
    },

    getWidthValue(ax) {
      if (ax.type == 4) {
        return `mr-8`;
      }
      return "";
    },

    existsAxlesRow2(axles) {
      return axles && axles.size > 7;
    },

    getAxisNumberClass(axis) {
      if (axis && axis.overload) {
        return "axle-field-red";
      }
      return "axle-field";
    },

    async removeTagFlow(vehicle) {
      const id = Number(vehicle.id)
      const tagId = this.vehicleTags[id]
      if (!tagId) return

      await ArchivalMeasurementsService.removeTag(
          id,
          tagId,
          vehicle.location.id,
          this.getCreatedAt(vehicle)
      )

      delete this.vehicleTags[id]
      this.selectedVehicleIds = this.selectedVehicleIds.filter(v => v !== id)
    },

    /* ================= HELPERS ================= */

    hasViolation(vehicle) {
      const flags = [
        'overloadSA',
        'overloadMA',
        'overloadTL',
        'overheight',
        'overlane',
        'overload',
        'overspeed',
        'wrongdirection',
      ]
      return flags.some(f => vehicle[f])
    },

    getAxisClass(axis) {
      if (!axis || !axis.type) return "";

      const baseClass = "axis-icon";
      const typeSuffix = axis.type > 1 ? `-${axis.type}` : "";
      const overloadSuffix = axis.overload ? "-ov" : "";

      const iconClass = "icon-a";
      const typeIconSuffix = axis.type > 1 ? `-${axis.type}` : "";
      const overloadIconSuffix = axis.overload ? "-ov" : "";

      return `${iconClass}${typeIconSuffix}${overloadIconSuffix} ${baseClass}${overloadSuffix}${typeSuffix}`;
    },

    getDivs(ax) {
      let divs = "";

      if (ax.type == 2 || ax.type == 3) {
        let totalLoad = (ax.totalLoad / 1000).toFixed(2);
        divs += `
          <div class="d-flex flex-column">
            <div class="row-cols-3 ${this.getAxisNumberClass(ax)} center-aligned">
              ${ax.type}
            </div>
            <div class="row-cols-6 center-aligned">
              <div class="${this.getAxisClass(ax)}"></div>
            </div>
            <div class="row-cols-3 ${this.getAxisNumberClass(ax)} center-aligned">
              ${totalLoad}
            </div>
          </div>`;
      } else {
        divs += `<div class="d-flex flex-rows">`;
        ax.axles.forEach((singleAxle) => {
          let singleTotalLoad = (singleAxle.totalLoad / 1000).toFixed(2);

          divs += `
            <div class="d-flex flex-column align-items-center ${this.getWidthValue(
              singleAxle
          )}" style="margin-right: 8px;">
              <div class="row-cols-3 ${this.getAxisNumberClass(
              singleAxle
          )} center-aligned">
                ${singleAxle.type > 3 ? "W" : singleAxle.type}
              </div>
              <div class="row-cols-6 center-aligned">
                <div class="${this.getAxisClass(singleAxle)}"></div>
              </div>
              <div class="row-cols-3 ${this.getAxisNumberClass(
              singleAxle
          )} center-aligned">
                ${singleTotalLoad}
              </div>
            </div>`;
        });
        divs += `</div>`;
      }

      return divs;
    },

    getWeightClassName(v) {
      return v.totalLoad >= v.limitTotalLoad
          ? 'measurement-row-field-b-red'
          : 'measurement-row-field-b'
    },

    getHeightClassName(v) {
      return v.overheight
          ? 'measurement-row-field-b-red'
          : 'measurement-row-field-b'
    },

    getCode(v) {
      return getCountryISO2(v) || ''
    },

    getPlate(v) {
      return v.plateLpr || '-----'
    },

    getCreatedAt(v) {
      return moment(v.createdAt).toISOString(false)
    },

    getFormattedDate(d) {
      return moment(d).add(this.momentDiffServer, 'ms').format('DD.MM.YYYY')
    },

    getFormattedTime(d) {
      return moment(d).add(this.momentDiffServer, 'ms').format('HH:mm:ss')
    },

    formatYesNo(value) {
      return value ? this.$t('yes') : this.$t('no')
    },

    hasOverload(v) {
      return Boolean(
          v?.overload ||
          Number(v?.overloadSA || 0) > 0 ||
          Number(v?.overloadMA || 0) > 0 ||
          Number(v?.overloadTL || 0) > 0
      )
    },

    hasAnyViolationFlag(v) {
      return Boolean(
          this.hasOverload(v) ||
          v?.overspeed ||
          v?.overheight ||
          v?.overlane
      )
    },

    formatViolationFlags(v) {
      const flags = []
      if (this.hasOverload(v)) flags.push('OVERLOAD')
      if (v?.overspeed) flags.push('OVERSPEED')
      if (v?.overheight) flags.push('OVERHEIGHT')
      if (v?.overlane) flags.push('OVERLANE')

      return flags.length ? flags.join(', ') : '-'
    },

    onPageSizeChange(event) {
      const nextPageSize = Number(event.target.value)
      const allowedOptions = this.pageSizeOptions.map(option => Number(option))

      if (!allowedOptions.includes(nextPageSize)) return

      this.$emit('page-size-change', nextPageSize)
    },
  },
}
</script>

<style scoped>
.vehicle-list-toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 10px 0;
  font-size: 14px;
}

.vehicle-list-page-size-label {
  color: #58585a;
  font-weight: 600;
}

.vehicle-list-page-size-select {
  min-width: 72px;
  padding: 4px 8px;
  border: 1px solid #b8b8b6;
  border-radius: 4px;
  background-color: white;
  color: #58585a;
  margin-right: 1rem;
}

.checkmark {
  color: green;
  font-size: 18px;
  font-weight: bold;
  margin-left: 60px;
}

.measurement-row {
  /* border-right: 1px solid #e1e1df; */
  border-right: 1px solid #b8b8b6;
  background: rgb(242, 240, 238);
  width: 50%;
  height: auto;
}

.measurement-row, .load {
  position: relative;
}

.measurement-row-header {
  font-weight: bold;
  font-size: 16px;
  background-color: white;
  color: black;
  height: 35px;
  padding: 5px 5px 0 5px;
}

.measurement-row-header-thin {
  font-weight: normal;
  font-size: 14px;
  background-color: white;
  border-left: 1px outset #cccccc;
  color: black;
  height: 35px;
  padding: 7px 5px 0 5px !important;
}

.measurement-row-row {
  font-size: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #b8b8b6;
  height: 26%;
}

.measurement-row-row .field-center {
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;
}

.top-pad {
  padding-top: 5px !important;
}

.measurement-row-body {
  height: 118px;
  background: rgb(242, 240, 238);
  overflow-x: auto;
  white-space: nowrap;
}

.measurement-row-row.field-form {
  border-bottom: 0;
  padding: 8px 0 8px;
}

.measurement-row-field {
  padding-left: 0.25rem;
  padding-top: 2px;
}

.measurement-row-field-b {
  font-weight: bold;
  font-size: 14px;
}

.measurement-row-field-b-red {
  font-weight: bold;
  font-size: 14px;
  color: red;
}

.width {
  align-items: center;
  margin-right: 8px !important;
}

.width-1 {
  width: 30px;
}
.width-2 {
  width: 60px;
}
.width-3 {
  width: 90px;
}
.width-4 {
  width: 140px;
}

.mr-8 {
  margin-right: 8px;
}

.field-icon {
  background-position-x: 80px;
  text-indent: -25px;
}

.vehicle-panel {
  width: 100%;
  margin-left: 0;
  position: relative; /* potrzebne dla overlayu */
}

.vehicle-panel .image {
  padding: 0;
  width: 20%;
  overflow: hidden;
  align-self: stretch;
  display: flex;
  align-items: center;
  justify-content: center;
}

.load {
  width: 30%;
}

.centered-flex {
  display: flex;
  justify-content: center;
  align-items: center;
}

.plate-flag {
  background: #204499;
  color: #f5f4f2;
  font-weight: bold;
  font-size: 12px;
  width: 45px;
  height: 35px;
}

.plate-flag .country-flag {
  height: 17.5px;
}

.plate {
  background: white;
  color: black;
  font-weight: bold;
  font-size: 21px;
  height: 35px;
  padding: 1px 5px !important;
}

.plate-item {
  height: 35px;
}

.plate-item .measurement-row-header {
  padding-top: 0;
}

.axle-row .col-1 {
  padding: 0 15px;
}

.axle-row .col-1:first-child {
  padding: 0;
}

.axle-field {
  font-size: 12px;
  background: rgb(242, 240, 238);
}

.axle-field-red {
  font-weight: bold;
  font-size: 12px;
  background: rgb(242, 240, 238);
  color: red;
}

.count2,
.count3,
.count4,
.count5 {
  margin-top: 25px;
}

.count6,
.count7,
.count8,
.count9,
.count10 {
  margin-top: 25px;
}

.with-margin-8 {
  margin-right: 8px;
}

.items-centered-axles {
  width: 30px;
}

.vehicles-list {
  width: 100%;
  margin: 0 auto;
  border-bottom: 1px solid #e1e1df;
}

.vehicles-list:first-child {
  border-top: 1px solid #e1e1df;
}

.measurement-row-row:last-child {
  border-bottom: none;
}

.row {
  margin: 0;
  padding: 0;
  display: flex;
}

.row > * {
  margin: 0;
  padding: 0;
}

.vehicle-header {
  padding: 5px 0 0 5px;
}

.vehicle-header-2 {
  padding: 10px 0 0 5px;
}

.image-icon {
  height: 20px;
}

.measurement-row.violation::after, .load.violation::after {
  content: "";
  position: absolute;
  inset: 0;
  background: rgba(255, 80, 80, 0.18);
  pointer-events: none;
  z-index: 5;
}

/* gdy prawy panel ukryty (isSG=true), lewy ma zająć miejsce prawego */
.vehicle-panel.no-right-panel .measurement-row {
  width: 80%;
}

/* prawy i tak znika przez v-if, ale zostawiam dla pewności jeśli kiedyś zmienisz na v-show */
.vehicle-panel.no-right-panel .load {
  display: none;
}
</style>
